import type { SyntheticEvent } from 'react';
import React, { useEffect, useState } from 'react';

import MelodyModal from 'components/common/MelodyModal';
import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/common/shareModal.scss';

type ShareModalProps = {
  heading: string;
  contentLabel: string;
  isOpen: boolean;
  url: string;
  onClose: () => void;
  toCopyLabel?: string;
  didCopyLabel?: string;
};
type FormEvent = {
  currentTarget: { shareLink: HTMLInputElement };
} & SyntheticEvent;

const ShareModal = ({
  heading,
  contentLabel,
  isOpen,
  url,
  onClose,
  toCopyLabel = 'Copy link to your clipboard',
  didCopyLabel = 'Link has been copied to your clipboard'
}: ShareModalProps) => {
  const { testId } = useMartyContext();
  const [isCopied, setIsCopied] = useState(false);
  const onCopyShareLink = (e: SyntheticEvent) => {
    e.preventDefault();
    const {
      currentTarget: { shareLink }
    } = e as FormEvent;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    } else {
      // IE 11 Clipboard copy
      shareLink.select();
      shareLink.focus();
      document.execCommand('copy');
    }
    setIsCopied(true);
  };

  useEffect(() => {
    setIsCopied(false);
  }, [isOpen]);

  return (
    <MelodyModal
      buttonTestId="close"
      className={css.modal}
      heading={heading}
      contentLabel={contentLabel}
      headingTestId="shareLinkModalHeader"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <form method="post" className={css.shareFields} onSubmit={onCopyShareLink}>
        <input name="shareLink" type="text" aria-hidden={true} tabIndex={-1} readOnly={true} data-test-id={testId('shareUrl')} value={url} />
        <button
          // Autofocus is ok in this instance, as it happens on user interaction
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
          name="copyButton"
          aria-label={isCopied ? didCopyLabel : toCopyLabel}
          type="submit"
        >
          {isCopied ? 'Copied' : 'Copy Link'}
        </button>
      </form>
    </MelodyModal>
  );
};

export default ShareModal;
